import React, { useCallback, useState } from 'react'
import styled from 'styled-components'
import List from '~/components/list'
import questionnaireListDefinition from './list/questionnaireList.definition'
import { colors } from '~/design'
import useAction from '~/prix/react/hooks/action'
import { format } from 'date-fns'
import { parseDateTime } from '~/prix/types/dateTime'
import AddIcon from '~/components/icons/ui/24px_bold-add.svg'
import { useNavigate } from 'react-router'
import RemoveIcon from '~/components/icons/ui/24px_bold-remove.svg'
import EditIcon from '~/components/icons/ui/24px_edit-72.svg'
import useActionFunction from '~/prix/react/hooks/actionFunction'
import deleteQuestionnaireDefinition from './delete/questionnaireDelete.definition'
import { useMedia } from 'react-use'
import { ActionListProps, QuestionnaireListItemProps } from './questionnaireTypes'
import { toastSuccess } from '~/components/toasts'

function readTargetPublicLabel(
  targetPublic: QuestionnaireListItemProps['targetPublic'] | string | null | undefined,
) {
  if (!targetPublic) {
    return 'Todos'
  }

  if (typeof targetPublic !== 'string') {
    return targetPublic.label ?? 'Todos'
  }

  try {
    const parsedTargetPublic = JSON.parse(targetPublic) as { label?: string }
    return parsedTargetPublic.label ?? 'Todos'
  } catch {
    return targetPublic
  }
}

const TransparentButton = styled.button`
  display: flex;
  flex: 1;
  background-color: transparent;
  border: none;
  text-align: center;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 14px;
  cursor: pointer;

  &[disabled] {
    opacity: 0.6;
    background-color: transparent;
    border: none;
  }
`

const Wrapper = styled.div`
  background-color: #f5f5f5;
  flex-direction: column;

  @media (max-width: 870px) {
    padding-top: 30px;
  }

  .table {
  }

  .action-buttons {
    display: flex;
    align-items: center;
    gap: 5px;

    button {
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: ${colors.prixBlue};
      border-radius: 5px;
      padding: 6px;
      border: none;

      cursor: pointer;
    }
  }
`

export default function ListQuestionnaire() {
  const navigate = useNavigate()

  const firstBreak = useMedia('(max-width: 1230px)')
  const secondBreak = useMedia('(max-width: 1060px)')
  const thirdBreak = useMedia('(max-width: 540px)')
  const [reloadKey, setReloadKey] = useState<number>(0)

  const { callAction: callActionDeleteQuestionnaire } = useActionFunction(
    deleteQuestionnaireDefinition,
  )

  const deleteQuestionnaireAction = useCallback((questionnaire: any) => {
    const { resultPromise } = callActionDeleteQuestionnaire(questionnaire)
    resultPromise
      .then(result => {
        toastSuccess('Questionário removido com sucesso!')
      })
      .catch(error => {
        console.log(error)
      })

    return resultPromise
  }, [])

  const deleteQuestionnaire = (id: number) => {
    const con = window.confirm('Confirme a remoção do questionário')
    if (con === true) {
      deleteQuestionnaireAction({ id }).then(() => {
        setReloadKey(prevKey => prevKey + 1)
      })
    }
  }
  const listQuestionnaire = useAction(questionnaireListDefinition, {}, [reloadKey], {
    autoLoad: true,
    staleWhileRevalidate: false,
  })

  const data = listQuestionnaire.result?.items?.map(item => ({
    id: item.id,
    title: item.title,
    observation: item.observation ?? null,
    targetType: item.targetType === 'citizen' ? 'Cidadão' : undefined,
    targetPublic: readTargetPublicLabel(item.targetPublic),
    startDate: item.startDate ? format(parseDateTime(item.startDate), 'dd/LL/yyyy') : '',
  }))

  return (
    <Wrapper>
      <div className='table'>
        <List
          title='Lista de questionários'
          items={data}
          isLoading={listQuestionnaire.isLoading}
          extraButtons={
            <TransparentButton
              onClick={() => navigate('../questionnaire/create')}
              title='Cadastrar'
            >
              <AddIcon width={19} height={19} fill='#fff' />
            </TransparentButton>
          }
          fractions={
            firstBreak && !secondBreak
              ? {
                  title: 1.5 / 4,
                  targetPublic: 1 / 4,
                  startDate: 1 / 4,
                  action: 0.5 / 4,
                }
              : secondBreak && !thirdBreak
              ? {
                  title: 1.5 / 3,
                  startDate: 1 / 3,
                  action: 0.5 / 3,
                }
              : thirdBreak
              ? {
                  title: 1.25 / 3,
                  startDate: 1 / 3,
                  action: 0.75 / 3,
                }
              : {
                  title: 1.5 / 5,
                  targetType: 1 / 5,
                  targetPublic: 1 / 5,
                  startDate: 1 / 5,
                  action: 0.5 / 5,
                }
          }
          labels={
            firstBreak && !secondBreak
              ? {
                  title: 'Título',
                  targetPublic: 'Tipo de público',
                  startDate: 'Data de inicio',
                  action: 'Ações',
                }
              : secondBreak
              ? {
                  title: 'Título',
                  startDate: 'Data de inicio',
                  action: 'Ações',
                }
              : {
                  title: 'Título',
                  targetType: 'Tipo de alvo',
                  targetPublic: 'Tipo de público',
                  startDate: 'Data de inicio',
                  action: 'Ações',
                }
          }
          actions={(item: ActionListProps) => (
            <div className='action-buttons'>
              <button
                title='Editar'
                onClick={() => navigate(`../questionnaire/update/${item.id}`)}
              >
                <EditIcon width={14} height={14} fill='#fff' />
              </button>
              <button title='Remover' onClick={() => deleteQuestionnaire(item.id)}>
                <RemoveIcon width={14} height={14} fill='#fff' />
              </button>
            </div>
          )}
        />
      </div>
    </Wrapper>
  )
}
